import type { Editor } from '@tiptap/core';

// ═══════════════════════════════════════════════════════════════
// Najít a nahradit (sekce 2.1)
//
// Spec doporučuje TipTap search/replace extension. Ta ale není součástí
// oficiálního balíčku pro Fázi 1. Zde je vlastní implementace nad
// ProseMirror dokumentem, která hledá napříč textovými uzly — tedy korektně
// najde i shody rozdělené formátováním (např. částečně tučné slovo),
// protože pracuje s plochým textem celého dokumentu a mapuje pozice zpět
// do ProseMirror souřadnic.
//
// POZNÁMKA pro Claude Code: až bude k dispozici stabilní TipTap
// search/replace extension, lze tento modul nahradit — API (find/next/prev/
// replace/replaceAll) je záměrně malé, aby šlo vyměnit implementaci.
// ═══════════════════════════════════════════════════════════════

export interface Match {
  from: number;
  to: number;
}

export class FindReplace {
  private matches: Match[] = [];
  private current = -1;
  private lastQuery = '';
  private caseSensitive = false;

  constructor(private editor: Editor) {}

  /** Sestaví plochý text dokumentu s mapou pozic → ProseMirror pos. */
  private buildIndex(): { text: string; map: number[] } {
    let text = '';
    const map: number[] = [];
    this.editor.state.doc.descendants((node, pos) => {
      if (node.isText && node.text) {
        for (let i = 0; i < node.text.length; i++) {
          text += node.text[i];
          map.push(pos + i);
        }
      } else if (node.isBlock && text.length && text[text.length - 1] !== '\n') {
        // Blokové hranice reprezentuj jako oddělovač, ať se shoda nepřelije
        // přes odstavce. Oddělovač nemá reálnou pozici → -1.
        text += '\n';
        map.push(-1);
      }
      return true;
    });
    return { text, map };
  }

  /** Najde všechny shody. Vrací počet. */
  find(query: string, opts: { caseSensitive?: boolean } = {}): number {
    this.lastQuery = query;
    this.caseSensitive = !!opts.caseSensitive;
    this.matches = [];
    this.current = -1;
    if (!query) {
      this.clearHighlight();
      return 0;
    }

    const { text, map } = this.buildIndex();
    const haystack = this.caseSensitive ? text : text.toLowerCase();
    const needle = this.caseSensitive ? query : query.toLowerCase();

    let idx = haystack.indexOf(needle);
    while (idx !== -1) {
      const startPos = map[idx];
      const endPos = map[idx + needle.length - 1];
      // Přeskoč shody přetékající přes blokovou hranici (-1 v mapě).
      if (startPos !== -1 && endPos !== -1) {
        this.matches.push({ from: startPos, to: endPos + 1 });
      }
      idx = haystack.indexOf(needle, idx + 1);
    }

    if (this.matches.length) {
      this.current = 0;
      this.selectCurrent();
    } else {
      this.clearHighlight();
    }
    return this.matches.length;
  }

  count(): number {
    return this.matches.length;
  }

  currentIndex(): number {
    return this.current;
  }

  next(): void {
    if (!this.matches.length) return;
    this.current = (this.current + 1) % this.matches.length;
    this.selectCurrent();
  }

  prev(): void {
    if (!this.matches.length) return;
    this.current = (this.current - 1 + this.matches.length) % this.matches.length;
    this.selectCurrent();
  }

  /** Nahradí aktuální shodu. */
  replace(replacement: string): void {
    if (this.current < 0 || !this.matches[this.current]) return;
    const m = this.matches[this.current];
    this.editor
      .chain()
      .focus()
      .insertContentAt({ from: m.from, to: m.to }, replacement)
      .run();
    // Po nahrazení přepočti (pozice se posunuly).
    this.find(this.lastQuery, { caseSensitive: this.caseSensitive });
  }

  /** Nahradí všechny shody. Postupuje odzadu, aby pozice nekolidovaly. */
  replaceAll(replacement: string): number {
    if (!this.matches.length) return 0;
    const n = this.matches.length;
    const chain = this.editor.chain().focus();
    for (let i = this.matches.length - 1; i >= 0; i--) {
      const m = this.matches[i];
      chain.insertContentAt({ from: m.from, to: m.to }, replacement);
    }
    chain.run();
    this.find(this.lastQuery, { caseSensitive: this.caseSensitive });
    return n;
  }

  private selectCurrent(): void {
    const m = this.matches[this.current];
    if (!m) return;
    this.editor.chain().setTextSelection({ from: m.from, to: m.to }).scrollIntoView().run();
  }

  private clearHighlight(): void {
    // Zvýraznění řešíme přes nativní selekci (selectCurrent). Nic navíc.
  }

  reset(): void {
    this.matches = [];
    this.current = -1;
    this.lastQuery = '';
  }
}
